import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { credentialingService } from '../../services/credentialingService';
import CredentialsList from './components/CredentialsList';
import InsuranceCredentialingPanel from './components/InsuranceCredentialingPanel';
import ComplianceDocumentsPanel from './components/ComplianceDocumentsPanel';
import RenewalRemindersPanel from './components/RenewalRemindersPanel';
import CredentialModal from './components/CredentialModal';

const ProviderCredentialingManagement = () => {
  const [activeTab, setActiveTab] = useState('credentials');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadStats();
  }, [refreshTrigger]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await credentialingService?.getCredentialingStats();
      setStats(data);
    } catch (err) {
      setError(err?.message || 'Failed to load credentialing statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    { id: 'credentials', label: 'Licenses & Certifications', icon: 'Award' },
    { id: 'insurance', label: 'Insurance Credentialing', icon: 'Shield' },
    { id: 'documents', label: 'Compliance Documents', icon: 'FileText' },
    { id: 'reminders', label: 'Renewal Reminders', icon: 'Bell' }
  ];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Provider Credentialing Management
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage licenses, certifications, insurance credentialing, and compliance documentation
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowCredentialModal(true)}
            >
              Add Credential
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl border border-border p-4 shadow-elevation-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                    <Icon name="Award" size={20} className="text-primary" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">Total</span>
                </div>
                <p className="text-2xl font-bold text-foreground mb-1">{stats?.totalCredentials || 0}</p>
                <p className="text-xs text-muted-foreground">Active Credentials</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-success">{stats?.activeCredentials || 0} Active</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-warning">{stats?.expiringSoon || 0} Expiring</span>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-4 shadow-elevation-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-warning/10 rounded-lg">
                    <Icon name="AlertTriangle" size={20} className="text-warning" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">Urgent</span>
                </div>
                <p className="text-2xl font-bold text-warning mb-1">{stats?.expiringSoon || 0}</p>
                <p className="text-xs text-muted-foreground">Expiring Soon</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-destructive">{stats?.expired || 0} Expired</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">{stats?.upcomingReminders || 0} Reminders</span>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-4 shadow-elevation-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                    <Icon name="Shield" size={20} className="text-success" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">Insurance</span>
                </div>
                <p className="text-2xl font-bold text-foreground mb-1">{stats?.insuranceNetworks || 0}</p>
                <p className="text-xs text-muted-foreground">Insurance Networks</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-success">{stats?.approvedNetworks || 0} Approved</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-warning">{stats?.pendingNetworks || 0} Pending</span>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-4 shadow-elevation-2">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
                    <Icon name="FileText" size={20} className="text-secondary" />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">Documents</span>
                </div>
                <p className="text-2xl font-bold text-foreground mb-1">{stats?.totalDocuments || 0}</p>
                <p className="text-xs text-muted-foreground">Compliance Documents</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="text-primary">All Verified</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl border border-border shadow-elevation-2">
              <div className="border-b border-border">
                <div className="flex overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm font-medium transition-smooth whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 md:p-6">
                {activeTab === 'credentials' && <CredentialsList refreshTrigger={refreshTrigger} />}
                {activeTab === 'insurance' && <InsuranceCredentialingPanel refreshTrigger={refreshTrigger} />}
                {activeTab === 'documents' && <ComplianceDocumentsPanel refreshTrigger={refreshTrigger} />}
                {activeTab === 'reminders' && <RenewalRemindersPanel refreshTrigger={refreshTrigger} />}
              </div>
            </div>
          </>
        )}
      </div>
      {showCredentialModal && (
        <CredentialModal
          isOpen={showCredentialModal}
          onClose={() => setShowCredentialModal(false)}
          onSuccess={handleRefresh}
        />
      )}
    </div>
  );
};

export default ProviderCredentialingManagement;